/* eslint-disable react/jsx-props-no-spreading */
import { TextField, Autocomplete, CircularProgress } from "@mui/material"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import { ReactElement, SyntheticEvent } from "react"

import { ITEM_BORDER_MULTIPLIER } from "../../../themes/defaultThemeParams"

type TAppControlledSelectProps = {
    value: string | number | null
    setValue: (
        e_: SyntheticEvent<Element, Event>,
        nv: string | number | null
    ) => void
    options: readonly (string | number)[]
    label: string
    loading: boolean
    disabled?: boolean
    disabledClearable?: boolean
    fullWidth?: boolean
}

/**
 * Controlled, reusable single select input. Options are supplied as array
 * of strings / numbers. This autocomplete uses option text highlight matching during
 * search.
 */
const AppControlledSelect = ({
    value,
    setValue,
    options,
    label,
    loading,
    disabled = false,
    disabledClearable = false,
    fullWidth = false,
}: TAppControlledSelectProps): ReactElement => (
    <Autocomplete
        value={value}
        onChange={setValue}
        disabled={disabled}
        disableClearable={disabledClearable}
        disablePortal
        options={options}
        getOptionLabel={(option) => String(option)}
        loading={loading}
        fullWidth={fullWidth}
        renderOption={(props, option, { inputValue }) => {
            const matches = match(String(option), inputValue, {
                insideWords: true,
            })
            const parts = parse(String(option), matches)

            return (
                <li {...props}>
                    <div>
                        {parts.map((part, index) => (
                            <span
                                // eslint-disable-next-line react/no-array-index-key
                                key={part.text + part.highlight + index}
                                style={{
                                    fontWeight: part.highlight ? 700 : 400,
                                }}
                            >
                                {part.text}
                            </span>
                        ))}
                    </div>
                </li>
            )
        }}
        renderInput={(params) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { InputLabelProps, InputProps, ...otherParams } = params
            return (
                <TextField
                    {...otherParams}
                    label={label}
                    placeholder="Click to select an option..."
                    InputProps={{
                        ...InputProps,
                        sx: { borderRadius: ITEM_BORDER_MULTIPLIER },
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    fullWidth={fullWidth}
                />
            )
        }}
    />
)
AppControlledSelect.defaultProps = {
    disabled: false,
    disabledClearable: false,
    fullWidth: false,
}

export default AppControlledSelect
